import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import {
  Badge, Banner, EmptyState, PageLoading, SearchInput, Select,
} from '../../components/ui';

type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'warning',
  confirmed: 'info',
  dispatched: 'info',
  delivered: 'success',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

interface PrimaryOrder {
  id: string;
  orderNo: string;
  orderDate: string;
  itemCount: number;
  totalAmount: number;
  status: string;
  items?: OrderLine[];
  statusHistory?: { status: string; at: string; by: string | null }[];
}

interface OrderLine {
  id: string;
  productId: string;
  name: string;
  code: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
}

interface Product {
  id: string;
  name: string;
  code: string;
  unit: string;
  price: number;
  category: string | null;
  brand: string | null;
}

/**
 * Primary orders — the distributor's purchase orders back to the company.
 *
 * This is the main commercial action for a distributor: they see the
 * secondary demand in their territory and place their own orders to
 * replenish stock. The form is simple — pick products, set quantities,
 * and submit.
 */
export default function DistributorPrimaryOrders() {
  const toast = useToast();
  const [orders, setOrders] = useState<PrimaryOrder[] | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<PrimaryOrder | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ orders: PrimaryOrder[] }>('/distributor/primary-orders');
      setOrders(res.orders);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load orders.');
      setOrders([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (orders ?? [])
      .filter((o) => !statusFilter || o.status === statusFilter)
      .filter((o) => !needle || o.orderNo.toLowerCase().includes(needle));
  }, [orders, search, statusFilter]);

  const totalValue = visible
    .filter((o) => o.status !== 'cancelled')
    .reduce((n, o) => n + o.totalAmount, 0);

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Primary orders</h1>
          <p className="topbar-sub">Your purchase orders to the company.</p>
        </div>
        <div className="row gap-8">
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} /> New order
          </button>
        </div>
      </header>

      <div className="page col gap-16">
        <div className="toolbar between">
          <div className="row gap-8">
            <SearchInput value={search} onChange={setSearch} placeholder="Search by order number" />
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
              <option value="">Any status</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Select>
          </div>
          {orders ? (
            <span className="small faint">
              {visible.length} order{visible.length === 1 ? '' : 's'} · ₹{(totalValue / 100).toLocaleString('en-IN')}
            </span>
          ) : null}
        </div>

        {!orders ? <PageLoading /> : visible.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="list"
              title={search || statusFilter ? 'No orders match that' : 'No primary orders yet'}
              body={search || statusFilter
                ? 'Try a different search or status.'
                : 'Place your first order to the company by clicking "New order" above.'}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th className="num">Lines</th>
                    <th className="num">Value</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((order) => (
                    <tr
                      key={order.id}
                      className="clickable"
                      onClick={() => setDetail(order)}
                    >
                      <td className="mono small nowrap">{order.orderNo}</td>
                      <td className="nowrap">{order.orderDate}</td>
                      <td className="num">{order.itemCount}</td>
                      <td className="num primary">₹{(order.totalAmount / 100).toLocaleString('en-IN')}</td>
                      <td>
                        <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>
                          {STATUS_LABEL[order.status] ?? order.status}
                        </Badge>
                      </td>
                      <td>
                        {order.status === 'pending' ? (
                          <CancelButton orderId={order.id} onCancelled={load} />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {creating ? (
        <CreatePrimaryOrderModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); }}
        />
      ) : null}

      {detail ? (
        <PrimaryOrderDetailModal
          order={detail}
          onClose={() => setDetail(null)}
          onChanged={load}
        />
      ) : null}
    </>
  );
}

// =====================================================================

function CancelButton({ orderId, onCancelled }: { orderId: string; onCancelled: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function cancel(e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm('Cancel this order? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.post(`/distributor/primary-orders/${orderId}/cancel`, {});
      toast.success('Order cancelled.');
      onCancelled();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not cancel.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className="btn btn-secondary btn-sm"
      disabled={busy}
      onClick={(e) => void cancel(e)}
    >
      Cancel
    </button>
  );
}

// =====================================================================

interface CartLine {
  productId: string;
  name: string;
  code: string;
  unit: string;
  price: number;
  quantity: number;
}

function CreatePrimaryOrderModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ products: Product[] }>('/distributor/catalogue')
      .then((res) => setProducts(res.products))
      .catch((err) => {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not load products.');
      });
  }, [toast]);

  function addProducts(ids: string[]) {
    if (!products) return;
    const existing = new Set(cart.map((c) => c.productId));
    const newLines: CartLine[] = ids
      .filter((id) => !existing.has(id))
      .map((id) => {
        const p = products.find((pr) => pr.id === id)!;
        return { productId: p.id, name: p.name, code: p.code, unit: p.unit, price: p.price, quantity: 1 };
      });
    if (newLines.length > 0) setCart((prev) => [...prev, ...newLines]);
  }

  function updateQuantity(productId: string, quantity: number) {
    setCart((prev) => prev.map((c) => c.productId === productId ? { ...c, quantity: Math.max(0, quantity) } : c));
  }

  function removeLine(productId: string) {
    setCart((prev) => prev.filter((c) => c.productId !== productId));
  }

  const total = cart.reduce((n, c) => n + c.price * c.quantity, 0);
  const validCart = cart.filter((c) => c.quantity > 0);

  async function submit() {
    if (validCart.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/distributor/primary-orders', {
        clientOrderId: crypto.randomUUID(),
        items: validCart.map((c) => ({ productId: c.productId, quantity: c.quantity })),
      });
      toast.success('Primary order placed.');
      onCreated();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError('Could not place order.');
    } finally {
      setBusy(false);
    }
  }

  const inCart = new Set(cart.map((c) => c.productId));

  return (
    <>
      <Modal
        title="New primary order"
        subtitle="Add products and set quantities to place your order."
        onClose={onClose}
        wide
        footer={(
          <>
            <span className="grow" style={{ fontWeight: 600 }}>
              Total: ₹{(total / 100).toLocaleString('en-IN')}
            </span>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || validCart.length === 0}
              onClick={() => void submit()}
            >
              {busy ? <span className="spinner" /> : null}
              Place order ({validCart.length} item{validCart.length === 1 ? '' : 's'})
            </button>
          </>
        )}
      >
        <div className="col gap-16">
          {error ? <Banner kind="danger">{error}</Banner> : null}

          {!products ? <PageLoading /> : (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPicking(true)}
              >
                <Icon name="plus" size={14} /> Add products
              </button>

              {cart.length === 0 ? (
                <p className="faint" style={{ textAlign: 'center', padding: '20px 0' }}>
                  Click "Add products" to select items for your order.
                </p>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Code</th>
                        <th>Unit</th>
                        <th className="num">Price</th>
                        <th className="num" style={{ width: 100 }}>Qty</th>
                        <th className="num">Line total</th>
                        <th aria-label="Remove" />
                      </tr>
                    </thead>
                    <tbody>
                      {cart.map((line) => (
                        <tr key={line.productId}>
                          <td className="primary">{line.name}</td>
                          <td className="mono small">{line.code}</td>
                          <td>{line.unit}</td>
                          <td className="num">₹{(line.price / 100).toLocaleString('en-IN')}</td>
                          <td className="num">
                            <input
                              type="number"
                              className="input"
                              min={0}
                              step={1}
                              value={line.quantity}
                              onChange={(e) => updateQuantity(line.productId, Number(e.target.value))}
                              style={{ width: 80, textAlign: 'right' }}
                            />
                          </td>
                          <td className="num primary">
                            ₹{((line.price * line.quantity) / 100).toLocaleString('en-IN')}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="icon-btn danger"
                              title="Remove"
                              onClick={() => removeLine(line.productId)}
                            >
                              <Icon name="trash" size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {picking && products ? (
        <ProductPickerModal
          products={products}
          alreadySelected={inCart}
          onDone={(ids) => { addProducts(ids); setPicking(false); }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </>
  );
}

// =====================================================================

function ProductPickerModal({ products, alreadySelected, onDone, onClose }: {
  products: Product[];
  alreadySelected: Set<string>;
  onDone: (ids: string[]) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [products]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return products
      .filter((p) => !alreadySelected.has(p.id))
      .filter((p) => !categoryFilter || p.category === categoryFilter)
      .filter((p) => !needle
        || p.name.toLowerCase().includes(needle)
        || p.code.toLowerCase().includes(needle));
  }, [products, alreadySelected, search, categoryFilter]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (visible.every((p) => selected.has(p.id))) {
      setSelected((prev) => {
        const next = new Set(prev);
        visible.forEach((p) => next.delete(p.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        visible.forEach((p) => next.add(p.id));
        return next;
      });
    }
  }

  const allChecked = visible.length > 0 && visible.every((p) => selected.has(p.id));

  return (
    <Modal
      title="Select products"
      subtitle={selected.size > 0 ? `${selected.size} selected` : 'Search and tick the products you want to order.'}
      onClose={onClose}
      wide
      footer={(
        <>
          <span className="grow small faint">
            {visible.length} product{visible.length === 1 ? '' : 's'} shown
          </span>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selected.size === 0}
            onClick={() => onDone(Array.from(selected))}
          >
            Add {selected.size} product{selected.size === 1 ? '' : 's'}
          </button>
        </>
      )}
    >
      <div className="col gap-12">
        <div className="row gap-8">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name or code…"
          />
          {categories.length > 1 ? (
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Category"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          ) : null}
        </div>

        <div className="table-wrap" style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    title="Select all"
                  />
                </th>
                <th>Product</th>
                <th>Code</th>
                <th>Unit</th>
                <th className="num">Price</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr
                  key={p.id}
                  className="clickable"
                  onClick={() => toggle(p.id)}
                  style={selected.has(p.id) ? { background: 'var(--brand-soft)' } : undefined}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="primary">{p.name}</td>
                  <td className="mono small">{p.code}</td>
                  <td>{p.unit}</td>
                  <td className="num">₹{(p.price / 100).toLocaleString('en-IN')}/{p.unit}</td>
                </tr>
              ))}
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={5} className="faint" style={{ textAlign: 'center', padding: 20 }}>
                    {alreadySelected.size > 0 && !search && !categoryFilter
                      ? 'All products are already added to your order.'
                      : 'No products match your search.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

// =====================================================================

function PrimaryOrderDetailModal({ order, onClose, onChanged }: {
  order: PrimaryOrder;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [full, setFull] = useState<PrimaryOrder | null>(null);

  useEffect(() => {
    api.get<PrimaryOrder>(`/distributor/primary-orders/${order.id}`)
      .then(setFull)
      .catch((err) => {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not load order.');
      });
  }, [order.id, toast]);

  async function cancel() {
    if (!window.confirm('Cancel this order? This cannot be undone.')) return;
    try {
      await api.post(`/distributor/primary-orders/${order.id}/cancel`, {});
      toast.success('Order cancelled.');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not cancel.');
    }
  }

  return (
    <Modal
      title={`Order ${order.orderNo}`}
      onClose={onClose}
      wide
      footer={order.status === 'pending' ? (
        <button type="button" className="btn btn-secondary" onClick={() => void cancel()}>
          Cancel order
        </button>
      ) : undefined}
    >
      <div className="col gap-16">
        <div className="row gap-16" style={{ flexWrap: 'wrap' }}>
          <div><span className="small faint">Date</span><div>{order.orderDate}</div></div>
          <div>
            <span className="small faint">Status</span>
            <div>
              <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>
                {STATUS_LABEL[order.status] ?? order.status}
              </Badge>
            </div>
          </div>
          <div>
            <span className="small faint">Value</span>
            <div style={{ fontWeight: 600 }}>₹{(order.totalAmount / 100).toLocaleString('en-IN')}</div>
          </div>
        </div>

        {!full ? <PageLoading /> : (
          <>
            {full.items && full.items.length > 0 ? (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Code</th>
                      <th className="num">Qty</th>
                      <th>Unit</th>
                      <th className="num">Price</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {full.items.map((item, i) => (
                      <tr key={i}>
                        <td className="primary">{item.name}</td>
                        <td className="mono small">{item.code}</td>
                        <td className="num">{item.quantity}</td>
                        <td>{item.unit}</td>
                        <td className="num">₹{(item.unitPrice / 100).toLocaleString('en-IN')}</td>
                        <td className="num primary">₹{(item.lineTotal / 100).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>Total</td>
                      <td className="num primary" style={{ fontWeight: 600 }}>
                        ₹{(order.totalAmount / 100).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : null}

            {full.statusHistory && full.statusHistory.length > 0 ? (
              <div className="col gap-8">
                <h4 style={{ margin: 0 }}>History</h4>
                {full.statusHistory.map((h, i) => (
                  <div key={i} className="row gap-8 small">
                    <Badge tone={STATUS_TONE[h.status] ?? 'neutral'}>
                      {STATUS_LABEL[h.status] ?? h.status}
                    </Badge>
                    <span className="faint">{new Date(h.at).toLocaleString()}</span>
                    {h.by ? <span className="faint">by {h.by}</span> : null}
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
